import React, { ReactNode } from "react";
import { Label } from "src/components/ui/Label";
import { Input } from "src/components/ui/Input";
import { Textarea } from "src/components/ui/TextArea";
import { cn } from "src/lib/utils";

/**
 * FormField component for consistent form field styling
 */
interface FormFieldProps {
  /** Label for the form field */
  label: string;
  /** ID for the form field (used for label association) */
  id: string;
  /** Children to render (typically an Input component) */
  children: ReactNode;
  /** Optional help text to display below the field */
  helpText?: string;
  /** Additional CSS class for the field container */
  className?: string;
  /** Additional CSS class for the label */
  labelClassName?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  id,
  children,
  helpText,
  className,
  labelClassName
}) => {
  return (
    <div className={cn("space-y-2 w-full", className)}>
      <div className="flex items-center">
        <Label htmlFor={id} className={cn("text-sm font-medium block", labelClassName)}>
          {label}
        </Label>
      </div>
      {children}
    </div>
  );
};

/**
 * FormInput component for consistent input styling
 */
interface FormInputProps {
  /** Label for the input field */
  label: string;
  /** ID for the input field */
  id?: string;
  /** Type of input (input or textarea) */
  type?: string;
  /** Value of the input */
  value?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Help text to display */
  helpText?: string;
  /** Additional CSS class for the field container */
  containerClassName?: string;
  /** Additional CSS class for the label */
  labelClassName?: string;
  /** Additional CSS class for the input/textarea */
  valueClassName?: string;
  /** Number of rows for textarea */
  rows?: number;
  /** onChange handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Other input attributes */
  [key: string]: any;
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  id,
  type = "text",
  helpText,
  containerClassName,
  labelClassName,
  valueClassName,
  rows,
  ...props
}) => {
  const inputId = id || props.name || label.toLowerCase().replace(/\s+/g, '_');
  const isTextarea = type === "textarea";

  return (
    <FormField
      label={label}
      id={inputId}
      helpText={helpText}
      className={containerClassName}
      labelClassName={labelClassName}
    >
      {isTextarea ? (
        <Textarea
          id={inputId}
          className={cn("w-full bg-white", valueClassName)}
          rows={rows}
          {...props}
        />
      ) : (
        <Input
          id={inputId}
          type={type}
          className={cn("w-full bg-white", valueClassName)}
          {...props}
        />
      )}
    </FormField>
  );
};

export default FormInput;